import React, { useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { IconButton, Theme } from "@mui/material";
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CloseIcon from "@mui/icons-material/Close";
import * as actions from "../../actionCreator";
import { GlobalApplicationState } from "globalApplicationState";
import { eventsApi } from 'api/instances';
import Loading from "modules/common/components/loading";
import LoadingOverlay from "modules/common/components/loadingOverlay";
import { Answers, AttendanceType, EventView as IEventView, RSVPType } from "../../models";
import SnackbarWrapper from "modules/common/components/snackbars/snackbarWrapper";
import { ImageScale } from "modules/posts/models";
import { EventViewContent } from "./eventViewContent";
import { useDefaultLocale } from "modules/common/hooks/useDefaultLocale";

import '../../../../styles/views.sass'

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        close: {
            position: "absolute",
            top: 0,
            right: 0,
            color: "#ffffff",
            zIndex: 2,
        },
    })
);

const EventView: React.FunctionComponent<PropsWithRedux> = ({
    eventId,
    tinyMceCustomCssEnabled,
    getEvent: proppedGetEvent,
    onClose,
    ...props
}) => {
    const classes = useStyles();
    const [activeLcid, setActiveLcid] = useState<string>("en-us");
    const [event, setEvent] = useState<IEventView>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    useDefaultLocale();
    const currentEventId = event?.id;

    useEffect(() => {
        const getEvent = async (eventId: string, initialLoad?: boolean) => {
            const event = await proppedGetEvent(eventId, ImageScale.Modal);

            if (!!event) {
                const newLcid: string = !!event.translatedContent![event.preferredLCID]
                    ? event.preferredLCID
                    : "en-us";

                setActiveLcid(newLcid)
                setEvent(event);

                if (initialLoad)
                    eventsApi.sendEventOpenedEvent(eventId, activeLcid);
            }

            setIsLoading(false);
        }

        if (eventId && eventId !== currentEventId) {
            getEvent(eventId, true);
        }
    }, [eventId, proppedGetEvent, activeLcid, currentEventId]);

    useEffect(() => {
        return () => {
            if (event)
                eventsApi.sendEventClosedEvent(event.id, event.preferredLCID);
        }
    }, [event]);

    const onSignUp = async (attendanceType: AttendanceType, answers: Answers): Promise<IEventView> => {
        let rsvpType: RSVPType = "initial";

        if (event?.currentUserResponse && !event.isCurrentUserNotAttending && !event.isCurrentUserOnWaitlist) {
            if (attendanceType === "InPerson" && !event.isCurrentUserAttendingInPerson)
                rsvpType = "changeStatus";
            else if (attendanceType === "Online" && !event.isCurrentUserAttendingOnline)
                rsvpType = "changeStatus";
            else if (attendanceType === "Waitlist" || attendanceType === "NotAttending")
                rsvpType = "initial";
            else
                rsvpType = "editAnswers";
        }

        const newEvent = await props.rsvp(eventId, attendanceType, answers, rsvpType);

        if (newEvent) {
            setEvent(newEvent);

            props.updateEventFeed(
                props.eventsFeed.map(event => event.id === eventId
                    ? {
                        ...event,
                        currentUserResponse: {
                            answers,
                            attendanceType
                        },
                        isCurrentUserAttending: attendanceType === "InPerson" || attendanceType === "Online",
                        isCurrentUserAttendingInPerson: attendanceType === "InPerson",
                        isCurrentUserAttendingOnline: attendanceType === "Online",
                        isCurrentUserNotAttending: attendanceType === "NotAttending",
                        isCurrentUserOnWaitlist: attendanceType === "Waitlist"
                    }
                    : event
                )
            );
        }

        return newEvent;
    }

    return (
        <>
            <IconButton onClick={onClose} className={classes.close} size="large">
                <CloseIcon color="inherit" />
            </IconButton>
            {isLoading
                ? <Loading />
                : event
                    ? <>
                        <EventViewContent
                            event={event}
                            onSignUp={onSignUp}
                        />
                        {/* success snackbar */}
                        <SnackbarWrapper
                            open={!!props.infoMessage.message}
                            message={props.infoMessage.message}
                            severity={props.infoMessage.severity}
                            onClose={props.clearSuccessMessage}
                            icon={
                                props.infoMessage.icon === "cancel" ? (
                                    <CancelOutlinedIcon fontSize="inherit" />
                                ) : undefined
                            }
                        />
                        {/* error snack bar */}
                        <SnackbarWrapper open={!!props.errorMessage} onClose={props.clearErrorMessage} message={props.errorMessage} severity="error" />
                        <LoadingOverlay show={props.isSaving} styles={{ paddingTop: 150 }} />
                    </>
                    : <div>
                        <div className="no-event-data">Unfortunately, this event is no longer available.</div >
                    </div>
            }
        </>
    );
}

interface Language {
    language: string;
    lcid: string;
}

interface IEventViewProps {
    eventId: string;
    onClose?: () => void;
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: IEventViewProps) => ({
        ...ownProps,
        errorMessage: state.events.errorMessage,
        eventsFeed: state.events.eventsFeed.eventsFeed,
        isFetching: state.events.isFetching,
        isSaving: state.events.isSaving,
        infoMessage: state.events.infoMessage,
        tinyMceCustomCssEnabled: state.settings.clientSettings.tinyMceCustomCssEnabled,
        tenantName: state.settings.clientSettings.displayName
    }),
    {
        clearErrorMessage: actions.clearErrorMessage,
        clearSuccessMessage: actions.clearSuccessMessage,
        getEvent: actions.getEvent,
        rsvp: actions.rsvp,
        updateEventFeed: actions.updateEventFeed
    }
);
type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(EventView);
